import * as React from "react";
import styled from "@emotion/styled";
import {
  Button,
  Container,
  CtaCentered,
  FlexRow,
  H2,
  IconCheckRounded,
  Stack,
  Text,
} from "atoms";
import brush from "assets/images/hug-brush-7.svg";
import { useFollow } from "features/follows";
import { GenericImage } from "features/images";
import { CollectiveFeed } from "features/posts";
import { useCollectiveArtists } from "features/reviewable";
import { CollectiveReviews } from "features/reviews";
import { SuccessMessage } from "features/reviewable/components/ArtistForm";
import { CollectiveArtistList } from "./CollectiveArtistList";
import { CollectiveHeader } from "./CollectiveHeader";
import type { Collective } from "../types";

const perPage = 12;

type CollectiveTemplateProps = {
  collective: Collective;
};

function CollectiveTemplate({ collective }: CollectiveTemplateProps) {
  const { id, badge, banner, name, hasCollectivePage, affiliatedArtists } =
    collective;

  const { data } = useCollectiveArtists(id);

  const { follow: handleFollowAll, isSuccess } = useFollow({
    badge: id,
  });

  // Get total artist count directly from collective, not array of visible
  const artistCount = affiliatedArtists.length;

  // Show subset of returned artists
  const [numVisible, setNumVisible] = React.useState(perPage);
  const artists = data?.artists.slice(0, numVisible) ?? [];
  const loadMore =
    numVisible >= artistCount
      ? undefined
      : () => setNumVisible((prev) => prev + perPage);

  const image1Url =
    artists?.[0]?.type === "ARTIST"
      ? artists?.[0].artistCollections[0].collectionImage?.url600
      : banner?.url600;
  const image2Url =
    artists?.[1]?.type === "ARTIST"
      ? artists?.[1].artistCollections[0].collectionImage?.url600
      : badge.url600;

  return (
    <>
      <CollectiveHeader
        artistCount={artistCount}
        handleFollowAll={hasCollectivePage ? handleFollowAll : undefined}
        collective={collective}
      />
      <Stack gap="xxl">
        {hasCollectivePage && <CollectiveFeed id={id} />}

        <section style={{ position: "relative", zIndex: 2 }}>
          <Brush src={brush} />
          <Container width="lg">
            <Stack gap="md">
              {hasCollectivePage && (
                <FlexRow flexDirection="column" alignItems="center" gap="0">
                  <H2 id="artists">{name} Artists</H2>
                  <Button size="xs" onClick={handleFollowAll}>
                    Follow All Artists in {name}
                  </Button>
                </FlexRow>
              )}
              <CollectiveArtistList artists={artists} loadMore={loadMore} />
            </Stack>
          </Container>
        </section>

        <SSection>
          <Container width="lg">
            <Stack>
              <CollectiveReviews id={id} name={name} />
            </Stack>
          </Container>
        </SSection>

        {hasCollectivePage && (
          <Container width="lg">
            <SCtaCentered bg="fg" image1={image1Url} image2={image2Url}>
              <H2>
                Never miss an opportunity from <TextNoWrap>{name}</TextNoWrap>{" "}
                Artists
              </H2>
              <Text>
                By following all the artists in {name} you will get updates
                about future drops, announcements, events, and&nbsp;more!
              </Text>
              <Button onClick={handleFollowAll} variant="secondary" size="sm">
                Follow All {name} Artists
              </Button>
            </SCtaCentered>
          </Container>
        )}
      </Stack>

      {isSuccess && (
        <FollowSuccess duration={6}>
          <IconCheckRounded /> You followed all {artistCount} artists
        </FollowSuccess>
      )}
    </>
  );
}

const SCtaCentered = styled(CtaCentered)(({ theme }) => ({
  "&&": { marginBottom: "5rem", position: "relative", zIndex: 4 },
  "a:link": { color: theme.colors.fg },
}));

const TextNoWrap = styled.span({
  whiteSpace: "nowrap",
});

const SSection = styled.section({
  position: "relative",
  zIndex: 3,
});

const FollowSuccess = styled(SuccessMessage)({
  position: "fixed",
  left: "50%",
  top: "50%",
  transform: "translate(-50%, -50%)",
  fontSize: 16,
  fontWeight: "bold",
  minHeight: "3rem",
});

const Brush = styled(GenericImage)({
  position: "absolute",
  left: "50%",
  top: 0,
  width: "105vw",
  transform: "translate(-50%, -25%)",
  zIndex: 0,
  pointerEvents: "none",
  opacity: 0.05,
  objectFit: "none",
});

export { CollectiveTemplate };
